<template>
  <div class="chatbot-chat-container">
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p>Loading chat session...</p>
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <div class="alert alert-danger">
        <h4>Error Loading Chat Session</h4>
        <p>{{ error }}</p>
        <button @click="goBackToSessions" class="btn btn-primary">Back to Sessions</button>
      </div>
    </div>
    
    <!-- Main Content -->
    <div v-else class="chat-layout">
      <!-- Chat Session Header -->
      <ChatSessionHeader
        :selected-candidate="selectedCandidate"
        :chat-session="chatSession"
        :session-files="sessionFiles"
        :selected-file="selectedFile"
        :messages="messages"
        @files-added="handleFilesAdded"
        @file-removed="handleFileRemoved"
        @file-selected="handleFileSelection"
      />

      <div class="chat-main-container">
        <div class="chat-component-wrapper">
      <ChatbotChat 
            ref="chatbotChat"
            :messages="messages"
            :selected-file="selectedFile"
            :session-files="sessionFiles"
            :is-session-mode="true"
            :is-processing-message="isProcessingMessage"
            :candidate-id="candidateId"
            :session-id="sessionId"
            @send-message="sendMessage"
            @connection-change="handleConnectionChange"
            @query-complete="completeQuery"
            @query-error="failQuery"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatSessionHeader from './components/ChatSessionHeader.vue'
import ChatbotChat from './components/ChatbotChat.vue'
import ChatbotService from "@/services/chatbot.service";

export default {
  name: 'ChatSessionPage',
  components: { 
    ChatSessionHeader,
    ChatbotChat
  },
  props: {
    candidateId: {
      type: String,
      required: true
    },
    sessionId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      messages: [],
      selectedFile: null,
      selectedCandidate: {},
      chatSession: {},
      sessionFiles: [],
      loading: true,
      error: null,
      isProcessingMessage: false
    }
  },
  async mounted() {
    await this.initializeChatSession();
  },
  methods: {
    async initializeChatSession() {
      try {
        const candidate = await ChatbotService.getFinancingCandidate(this.candidateId);
        this.selectedCandidate = candidate;
        
        const session = await ChatbotService.getChatSessionHistory(this.candidateId, this.sessionId);
        console.log('session', session);
        this.chatSession = session;
        
        this.sessionFiles = session.files || [];
        
        this.messages = this.parseMessage(session.messages) || [];
        
        // Set first file as selected if available
        if (this.sessionFiles.length > 0) {
          this.selectedFile = this.sessionFiles[0];
        }
        
        this.loading = false;
      } catch (err) {
        this.error = 'Failed to load chat session.';
        console.error('Error loading chat session:', err);
        this.loading = false;
      }
    },

    parseMessage(messages) {
      if (!messages || !Array.isArray(messages)) return [];
      
      return messages.map(message => {
        return {
          role: message.type || message.role,
          content: message.content ? message.content: ''
        }
      }).filter(message => message.content); // Remove empty messages
    },
    
    async handleFileAdded(file) {
      try {
        this.sessionFiles.push(file);
        
        // Update the selected file if none is selected
        if (!this.selectedFile) {
          this.selectedFile = file;
        }
        
        await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
          files: this.sessionFiles
        });
        
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      } catch (err) {
        console.error('Error adding file to session:', err);
      }
    },
    
    async handleFilesAdded(files) {
      try {
        // Add all files to the session
        this.sessionFiles.push(...files);
        
        // Update the selected file if none is selected
        if (!this.selectedFile && files.length > 0) {
          this.selectedFile = files[0];
        }
        
        // Call chatbot service to update the session with all files
        await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
          files: this.sessionFiles
        });
        
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      } catch (err) {
        console.error('Error adding files to session:', err);
        this.$bvToast.toast('Error adding files: ' + (err.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      }
    },
    
    async handleFileRemoved(fileId) {
      try {
        this.sessionFiles = this.sessionFiles.filter(f => f.id !== fileId);
        
        // Clear selected file if it was removed
        if (this.selectedFile && this.selectedFile.id === fileId) {
          this.selectedFile = this.sessionFiles.length > 0 ? this.sessionFiles[0] : null;
        }
        
        await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
          files: this.sessionFiles
        });
        
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      } catch (err) {
        console.error('Error removing file to session:', err);
      }
    },
    
    handleFileSelection(file) {
      this.selectedFile = file;
    },

    handleConnectionChange(status) {
      this.websocketStatus = status;
      switch (status) {
        case 'connected':
          this.websocketStatusText = 'Live';
          this.websocketStatusClass = 'status-connected';
          break;
        case 'error':
        case 'disconnected':
          this.websocketStatusText = 'Standard mode';
          this.websocketStatusClass = 'status-offline';
          break;
        default:
          this.websocketStatusText = 'Connecting…';
          this.websocketStatusClass = 'status-connecting';
      }
    },
    
    async sendMessage(input) {
      if (!input.trim()) return;
      
      // Add user message
      this.messages.push({ 
        role: 'user', 
        content: input.trim() 
      });
      
      // Set processing state
      this.isProcessingMessage = true;
      
      // Add processing message with animation
      this.messages.push({ 
        role: 'assistant', 
        content: 'Thinking...',
        status: 'processing'
      });
      
      // Update session with new messages
      await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
        messages: this.messages
      });
      
      this.$nextTick(() => {
        this.scrollChatToBottom();
      });
      
      // WebSocket path: ChatbotChat already sent the message; completion comes via @query-complete
      if (this.$refs.chatbotChat?.isWebSocketConnected()) {
        return;
      }

      // Fallback: HTTP send + polling when WebSocket is unavailable
      try {
        await ChatbotService.sendChatMessageToSession(this.candidateId, this.sessionId, {
          content: input.trim(),
          metadata: {}
        });
        await this.trackQueryWithPostProcessing();
      } catch (err) {
        console.error('Error sending message to session:', err);
        
        // Remove processing message and add error message
        this.messages.pop(); // Remove "Thinking..." message
        
        this.messages.push({ 
          role: 'assistant', 
          content: 'Sorry, there was an error sending your message. Please try again.' 
        });
        
        // Update session with error message
        await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
          messages: this.messages
        });
        
        this.isProcessingMessage = false;
        
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      }
    },
    
    async trackQueryWithPostProcessing() {
      const taskId = `${this.candidateId}/${this.sessionId}/query-status`;

      this.$taskTracker.trackTask(taskId, {
        onProgress: (data) => {
          if (data?.status && data.status !== 'completed') {
            const lastMessage = this.messages[this.messages.length - 1];
            if (lastMessage?.content === 'Thinking...' && data.message && data.message !== 'Thinking...') {
              lastMessage.content = data.message;
            }
          }
        },
        onSuccess: (data) => this.completeQuery(data),
        onError: () => this.failQuery(),
        onTimeout: () => this.handleQueryError('Query timed out. Please try again.')
      });
    },

    async completeQuery(data = {}) {
      const lastMessage = this.messages[this.messages.length - 1];

      if (lastMessage?.role === 'assistant') {
        if (data.result) {
          lastMessage.content = data.result.trim();
        }
        this.$delete(lastMessage, 'status');
        this.$delete(lastMessage, 'displayedHtml');
        this.$delete(lastMessage, 'displayedContent');
      }

      await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
        messages: this.messages
      });

      this.isProcessingMessage = false;
      this.$nextTick(() => this.scrollChatToBottom());
    },

    async failQuery() {
      if (this.messages.length > 0 && this.messages[this.messages.length - 1].content === 'Thinking...') {
        this.messages.pop();
      }

      this.messages.push({
        role: 'assistant',
        content: 'Sorry, there was an error processing your request. Please try again.'
      });

      await ChatbotService.updateChatSession(this.candidateId, this.sessionId, {
        messages: this.messages
      });

      this.isProcessingMessage = false;
      this.$nextTick(() => this.scrollChatToBottom());
    },

    handleQueryError(errorMessage) {
      if (this.messages.length > 0 && this.messages[this.messages.length - 1].content === 'Thinking...') {
        this.messages.pop();
      }
      
      this.messages.push({ 
        role: 'assistant', 
        content: errorMessage
      });
      
      this.isProcessingMessage = false;
      
      this.$nextTick(() => {
        this.scrollChatToBottom();
      });
    },

    scrollChatToBottom() {
      const chatBox = document.getElementById("chat");
      if (chatBox) chatBox.scrollTop = chatBox.scrollHeight;
    },


    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString();
      } catch (e) {
        return '';
      }
    },
    
    goBackToSessions() {
      this.$router.push({ 
        name: 'chatbot-candidate',
        params: { candidateId: this.candidateId }
      });
    },
    
  }
}
</script>

<style lang="scss" scoped>
@import '../chatbot/index.scss';

.chatbot-chat-container {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  min-height: 600px;
}

.chat-layout {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  min-height: 600px;
}

.chat-main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  min-height: 0;
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  max-height: 100vh;
  background-color: #f8f9fa;
  color: #343a40;
  text-align: center;
  padding: 20px;

  .spinner-border {
    width: 3rem;
    height: 3rem;
    margin-bottom: 10px;
  }

  p {
    font-size: 1rem;
    margin-bottom: 0;
  }

  .alert {
    max-width: 400px;
    margin-top: 15px;
  }
}

.chat-component-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}
</style> 